import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

const DEFAULT_SEQUENCE_FR = [
  "Analyse en cours…",
  "Croisement des sources de données…",
  "Application du framework Trusted Intelligence™…",
  "Génération de la synthèse IA…",
];
const DEFAULT_SEQUENCE_EN = [
  "Analysing…",
  "Cross-referencing data sources…",
  "Applying Trusted Intelligence™ framework…",
  "Generating AI synthesis…",
];

/**
 * Reusable AI thinking state.
 * Props:
 *   - lang: "fr" | "en"
 *   - steps: optional array of custom labels
 *   - loop: cycle indefinitely (default true)
 */
export function AIThinkingState({ lang = "fr", steps, loop = true, className = "" }) {
  const seq = steps || (lang === "fr" ? DEFAULT_SEQUENCE_FR : DEFAULT_SEQUENCE_EN);
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setI((prev) => (loop ? (prev + 1) % seq.length : Math.min(prev + 1, seq.length - 1)));
    }, 1600);
    return () => clearInterval(id);
  }, [seq.length, loop]);

  return (
    <div className={`relative overflow-hidden rounded-lg border border-[#E4E1D8] bg-white p-5 ${className}`} data-testid="ai-thinking-state">
      <div className="absolute inset-x-0 top-0 h-[1px] altiora-tracing" aria-hidden />
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="absolute -inset-2 rounded-full bg-[#0B2545]/8 blur-md" aria-hidden />
          <Sparkles className="relative h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#5B5B62]">
          {seq[i]}
        </div>
        <Loader2 className="ml-auto h-3.5 w-3.5 text-[#0B2545] animate-spin" strokeWidth={1.5} />
      </div>
      <div className="mt-4 space-y-2">
        {[0, 1, 2].map((r) => (
          <div key={r} className="h-2.5 rounded bg-[#EBE8DE] overflow-hidden relative">
            <div className="absolute inset-0 altiora-tracing" style={{ animationDelay: `${r * 0.25}s` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScoreRing({ value = 0, size = 200, stroke = 14, label, hint }) {
  const clamped = Math.max(0, Math.min(100, value || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius}
                className="altiora-ring-track" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={radius}
                className="altiora-ring-fill" strokeWidth={stroke} fill="none"
                strokeDasharray={circumference} strokeDashoffset={dashOffset} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono text-5xl text-[#1F2937] leading-none">{clamped}</div>
        {label && <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[#5B5B62]">{label}</div>}
        {hint && <div className="mt-2 text-xs text-[#8B8B8F]">{hint}</div>}
      </div>
    </div>
  );
}
